import React from 'react';
import { useDispatch } from 'react-redux';
import { Tabs, TabsProps } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import * as actions from '@actions';
import * as actionTypes from '@actionTypes';
import Assessment from '@components/Clinicians/Assessment';
import ERSreferral from '@components/Clinicians/Session/ERSreferral';
import styled from '@emotion/styled';
import GPHealthRecord from '@components/Clinicians/Session/GPHealthRecord';
import Documents from '@components/Clinicians/Session/Documents';
import { Survey } from 'vl-common/src/schemas/shared/survey';
import { useSessionAppointmentId } from '@hooks/useSessionAppointmentId';

type TabItemsType = TabsProps['items'];

const TabEnum = {
  DOCUMENTS: 'Documents',
  GP_CONNECT: 'GP Connect',
  ERS_REFERRAL: 'ERS Referral'
};

const TabsWrapper = styled.div`
  .ant-tabs-tab {
    padding-left: 1em;
    padding-right: 1em;
    margin-left: 0;
  }
`;

const Pad = styled.div`
  padding: 0 1em 1em 1em;
`;

const Background = () => {
  const dispatch = useDispatch();
  const appointmentId = useSessionAppointmentId(true);
  const { appointment } = useSessionAppointment();
  const {
    isLoading: isLoadingAssessmentSurveys,
    data: assessmentSurveys = [],
    refetch: refetchSurveys
  } = useQuery<Survey[]>({
    queryKey: ['getAssessmentSurveys', appointmentId],
    queryFn: async () => {
      const res = await dispatch(
        actions.getAssessmentSurveys({
          appt_id: String(appointmentId),
          response: 1
        })
      );

      return res.type === actionTypes.ASSESSMENT_SURVEY_SUCCESS && res.payload ? res.payload : [];
    }
  });

  // Stuff I don't know what to do with yet
  const appointmentCompleting = false;
  // @ts-ignore
  const ubrnActive = appointment?.ubrn?.length > 0 || false;

  // Might be a better way to handle this?
  const assessmentSurveyContents =
    assessmentSurveys.map((surveyRes) => ({
      key: surveyRes.surveyjs_id,
      label: surveyRes.shortname || surveyRes.title,
      children: (
        <Assessment
          key={surveyRes.surveyjs_id}
          case_review={appointment.case_review}
          pageType="clinician"
          surveyRes={surveyRes}
          refetch={async () => {
            await refetchSurveys();
          }}
          readonly={appointment.read_only}
          isLoadingSurveyRes={isLoadingAssessmentSurveys}
          apptCompleting={appointmentCompleting}
          showSurveyViewToggle
          auditReason={appointment.audit_reason_desc ?? undefined}
          auditText={appointment.audit_text_desc ?? undefined}
        />
      )
    })) || [];

  const tabItems: TabItemsType = [
    appointment?.ods_code && {
      key: TabEnum.GP_CONNECT,
      label: 'GP Health Record',
      children: Background.GpHealthRecord
    },
    ubrnActive && {
      key: TabEnum.ERS_REFERRAL,
      label: 'ERS Referral',
      children: Background.ErsReferral
    },
    ...assessmentSurveyContents,
    {
      key: TabEnum.DOCUMENTS,
      label: 'Documents',
      children: Background.Documents
    }
  ].filter(Boolean) as TabItemsType;

  return (
    <TabsWrapper>
      <Tabs items={tabItems} tabBarStyle={{ padding: '0 0em' }} />
    </TabsWrapper>
  );
};

Background.ErsReferral = (
  <Pad>
    <ERSreferral />
  </Pad>
);

Background.GpHealthRecord = (
  <Pad>
    <GPHealthRecord />
  </Pad>
);

Background.Documents = (
  <Pad>
    <Documents />
  </Pad>
);

export default Background;
